// Competitor comparison registry — powers the public /vs-[competitor] pages
// and the Landing comparison section. Persuasive but grounded in real shipped
// features (see /app/memory/LIVEPLAN_COMPARISON.md). Reused across competitors
// so adding a new "/vs-x" page is just a new entry here.
//
// Cell values: true (check) · false (dash) · string (qualifier, e.g. "Premium only").

export const TLB_COL = { key: "tlb", label: "Three Little Birds", featured: true };

// Execution-OS rows are identical across competitors (none of them offer it).
const EXECUTION_OS_ROWS = [
  { feature: "Launch board + Gantt roadmap with critical path", tlb: true, them: false },
  { feature: "Built-in CRM + deal pipeline → goals & runway", tlb: true, them: false },
  { feature: "Loan modeler + bank-ready PDF package", tlb: true, them: false },
  { feature: "Email & SMS campaigns", tlb: true, them: false },
  { feature: "Grants finder + application wizard", tlb: true, them: false },
  { feature: "Investor marketplace listing", tlb: true, them: false },
  { feature: "Founder Mentor + real-time collaboration", tlb: true, them: false },
  { feature: "Compliance tracker + launch-readiness score", tlb: true, them: false },
  { feature: "Document vault + receipt OCR", tlb: true, them: false },
  { feature: "Product & service feasibility analyzer (go / no-go)", tlb: true, them: false },
  { feature: "Business optimization recommendations", tlb: true, them: false },
  { feature: "Continuous process improvement tracker", tlb: true, them: false },
  { feature: "Brand Kit — fully branded PDF & deck exports", tlb: true, them: false },
  { feature: "White-label + advisor / referral tools", tlb: true, them: false },
];

export const COMPETITORS = {
  liveplan: {
    name: "LivePlan",
    slug: "vs-liveplan",
    // Feb 2027 · Per-competitor SEO copy so the /vs-* pages read
    // truly distinct in SERPs — not just a name-swapped template.
    metaTitle:
      "LivePlan vs. Three Little Birds (2026) — same plan builder, plus a full launch OS",
    metaDesc:
      "LivePlan plans and forecasts, then hands you back to a pile of spreadsheets. Three Little Birds writes the plan, models 3-year monthly financials with unlimited what-if scenarios, and then adds a Kanban + Gantt roadmap, CRM pipeline, grants finder and Founder Mentor — all for $15/mo (LivePlan starts at $20). Bring your QuickBooks or Xero actuals in one click and your plan-vs-actual dashboard fills itself in.",
    heroHeadline: "Everything LivePlan does — plus the part that actually launches your business.",
    heroSub:
      "LivePlan helps you write a plan and forecast. Three Little Birds does that too — at a lower price — and then keeps going: roadmap, CRM, campaigns, grants, compliance and an Founder Mentor that take you from idea to launch to growth in one place.",
    groups: [
      {
        label: "Plan & financials — we match LivePlan",
        rows: [
          { feature: "Three Little Birds business-plan writing", tlb: true, them: true },
          { feature: "Example-plan library to clone & customize", tlb: "50 plans", them: true },
          { feature: "Lender-ready 3-statement financials", tlb: true, them: true },
          { feature: "Driver-based forecasting (3-yr monthly P&L, CSV)", tlb: true, them: "Premium only" },
          { feature: "What-if scenario planner", tlb: true, them: "Premium only" },
          { feature: "Industry benchmarks with assumption flags", tlb: true, them: "Premium only" },
          { feature: "Investor pitch deck builder", tlb: true, them: true },
          { feature: "Plan-vs-actual + Three Little Birds Monthly Review", tlb: true, them: "Premium only" },
          { feature: "Editable .docx + email-to-banker export", tlb: true, them: false },
        ],
      },
      { label: "An execution OS LivePlan doesn't have", rows: EXECUTION_OS_ROWS },
      {
        label: "Pricing & access",
        rows: [
          { feature: "Starting price", tlb: "$15/mo", them: "$20/mo" },
          { feature: "Full suite (top tier)", tlb: "$35/mo", them: "$40/mo" },
          { feature: "Free preview — no card required", tlb: true, them: "Trial only" },
          { feature: "One tool from idea to launch to growth", tlb: true, them: false },
        ],
      },
    ],
  },

  bizplan: {
    name: "Bizplan",
    slug: "vs-bizplan",
    metaTitle:
      "Bizplan vs. Three Little Birds (2026) — Three Little Birds-native plan writing, no templates",
    metaDesc:
      "Bizplan gives you drag-and-drop templates and a Fundable investor pipeline. Three Little Birds writes a strategist-grade plan with Three Little Birds (no templates), ships lender-ready 3-statement financials and a driver-based 3-year monthly forecast, and then keeps going with a launch roadmap, CRM deal pipeline, grants finder, campaigns and an Founder Mentor — starting at $15/mo (Bizplan starts at $29).",
    heroHeadline: "More than a plan builder — the whole launch, in one place.",
    heroSub:
      "Bizplan gives you drag-and-drop templates and a fundraising network. Three Little Birds writes a strategist-grade plan with Three Little Birds, models defensible numbers, and then runs your launch — roadmap, CRM, grants, campaigns and an Founder Mentor — for less.",
    groups: [
      {
        label: "Plan & financials",
        rows: [
          { feature: "Three Little Birds business-plan writing", tlb: true, them: "Templates only" },
          { feature: "Example-plan library to clone & customize", tlb: "50 plans", them: "Templates only" },
          { feature: "Lender-ready 3-statement financials", tlb: true, them: true },
          { feature: "Driver-based forecasting (3-yr monthly P&L, CSV)", tlb: true, them: "Basic" },
          { feature: "What-if scenario planner", tlb: true, them: false },
          { feature: "Industry benchmarks with assumption flags", tlb: true, them: false },
          { feature: "Investor pitch deck builder", tlb: true, them: "Via Fundable" },
          { feature: "Plan-vs-actual + Three Little Birds Monthly Review", tlb: true, them: false },
        ],
      },
      { label: "An execution OS Bizplan doesn't have", rows: EXECUTION_OS_ROWS },
      {
        label: "Pricing & access",
        rows: [
          { feature: "Starting price", tlb: "$15/mo", them: "$29/mo" },
          { feature: "Free preview — no card required", tlb: true, them: "Trial only" },
          { feature: "Modern Three Little Birds-native experience", tlb: true, them: false },
          { feature: "One tool from idea to launch to growth", tlb: true, them: false },
        ],
      },
    ],
  },

  enloop: {
    name: "Enloop",
    slug: "vs-enloop",
    metaTitle:
      "Enloop vs. Three Little Birds (2026) — auto-scored plan meets the whole launch",
    metaDesc:
      "Enloop auto-generates a templated plan and scores it — that's step one. Three Little Birds ships a tailored, strategist-grade plan, defensible driver-based financials with what-if scenarios and industry benchmarks, and then the execution OS Enloop skips: Kanban + Gantt roadmap, CRM pipeline, grants finder, email + SMS campaigns and an Founder Mentor. Free 7-day preview, no card.",
    heroHeadline: "Auto-generated plans are step one. We take you to launch.",
    heroSub:
      "Enloop auto-writes a plan and scores it. Three Little Birds gives you a strategist-grade, tailored plan, defensible financials, and an entire execution OS — roadmap, CRM, grants, campaigns and an Founder Mentor — to actually launch and grow.",
    groups: [
      {
        label: "Plan & financials",
        rows: [
          { feature: "Three Little Birds business-plan writing", tlb: true, them: "Auto-text" },
          { feature: "Example-plan library to clone & customize", tlb: "50 plans", them: false },
          { feature: "Lender-ready 3-statement financials", tlb: true, them: true },
          { feature: "Driver-based forecasting (3-yr monthly P&L, CSV)", tlb: true, them: "Basic" },
          { feature: "What-if scenario planner", tlb: true, them: false },
          { feature: "Industry benchmarks with assumption flags", tlb: true, them: false },
          { feature: "Investor pitch deck builder", tlb: true, them: false },
          { feature: "Plan-vs-actual + Three Little Birds Monthly Review", tlb: true, them: false },
        ],
      },
      { label: "An execution OS Enloop doesn't have", rows: EXECUTION_OS_ROWS },
      {
        label: "Pricing & access",
        rows: [
          { feature: "Starting price", tlb: "$15/mo", them: "$11/mo" },
          { feature: "Tailored, strategist-grade writing", tlb: true, them: "Templated" },
          { feature: "Free preview — no card required", tlb: true, them: "7-day trial" },
          { feature: "One tool from idea to launch to growth", tlb: true, them: false },
        ],
      },
    ],
  },
};

export const competitorRowSlug = (feature) =>
  feature
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
